import React, { useState, useEffect } from "react";
import { FiX, FiStar } from "react-icons/fi";
import { apiEndpoints, publicFetch } from "../config/api";

const ReviewModal = ({ isOpen, onClose, booking }) => {
  const [formData, setFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    rating: 0,
    reviewMessage: "",
    bookingReference: "",
    serviceType: "safari",
  });
  const [customerPhotoUrl, setCustomerPhotoUrl] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Initialize form with booking data
  useEffect(() => {
    if (booking && isOpen) {
      setFormData({
        customerName: booking.customerName || "",
        customerEmail: booking.customerEmail || "",
        customerPhone: booking.customerPhone || "",
        rating: 0,
        reviewMessage: "",
        bookingReference: booking.bookingId || booking._id || "",
        serviceType: "safari",
      });
      setCustomerPhotoUrl(null);
      setPhotoPreview(null);
      setError("");
      setSuccess(false);
    }
  }, [booking, isOpen]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Compress image helper function
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8, maxSizeMB = 1) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              
              // If still too large, reduce quality further
              if (blob.size > maxSizeMB * 1024 * 1024) {
                canvas.toBlob(
                  (finalBlob) => {
                    if (!finalBlob) {
                      reject(new Error('Failed to compress image'));
                      return;
                    }
                    const compressedFile = new File([finalBlob], file.name, {
                      type: file.type || 'image/jpeg',
                      lastModified: Date.now(),
                    });
                    resolve(compressedFile);
                  },
                  file.type || 'image/jpeg',
                  0.6
                );
              } else {
                const compressedFile = new File([blob], file.name, {
                  type: file.type || 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              }
            },
            file.type || 'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle photo upload: POST /api/images/upload (form-data: title, category: "review", image) → use returned url for review
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Photo size should be less than 10MB (will be compressed)");
        return;
      }

      try {
        setError("");
        setIsUploadingPhoto(true);
        const compressedFile = await compressImage(file, 1920, 1920, 0.8, 1);

        const fd = new FormData();
        fd.append("title", formData.bookingReference ? `Review ${formData.bookingReference}` : "Review");
        fd.append("category", "review");
        fd.append("image", compressedFile);

        const response = await publicFetch(apiEndpoints.images.upload, {
          method: "POST",
          body: fd,
        });

        const result = await response.json();
        const url = result?.url;
        if (!response.ok || !url) {
          throw new Error(result?.message || result?.error || "Failed to upload photo");
        }

        setCustomerPhotoUrl(url);

        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(compressedFile);
      } catch (err) {
        console.error("Error uploading photo:", err);
        setError(err.message || "Failed to upload photo. Please try again.");
        setCustomerPhotoUrl(null);
        setPhotoPreview(null);
      } finally {
        setIsUploadingPhoto(false);
      }
    }
  };

  // Handle star rating
  const handleStarClick = (rating) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));
  };

  // Handle form submission: submit review with customerPhoto URL from images upload
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      if (!formData.customerName || !formData.customerEmail || !formData.customerPhone) {
        throw new Error("Please fill in all required customer information");
      }
      if (formData.rating === 0) {
        throw new Error("Please provide a rating");
      }
      if (!formData.reviewMessage.trim()) {
        throw new Error("Please provide a review message");
      }
      if (!customerPhotoUrl) {
        throw new Error("Please upload a customer photo");
      }

      const response = await publicFetch(apiEndpoints.reviews.base, {
        method: "POST",
        body: JSON.stringify({
          customerName: formData.customerName,
          customerEmail: formData.customerEmail,
          customerPhone: formData.customerPhone,
          rating: formData.rating,
          reviewMessage: formData.reviewMessage,
          bookingReference: formData.bookingReference,
          serviceType: formData.serviceType,
          customerPhoto: customerPhotoUrl,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        throw new Error(result.message || "Failed to submit review");
      }
    } catch (err) {
      setError(err.message || "An error occurred while submitting the review");
      console.error("Error submitting review:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ui-mdc-confirm-modal">
      <div className="ui-mdc-confirm-modal-content" style={{ maxWidth: "600px", width: "90%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="ui-mdc-confirm-modal-header">
          <h3 className="ui-mdc-confirm-modal-title">Add Review</h3>
          <button
            type="button"
            onClick={onClose}
            className="ui-mdc-confirm-modal-close"
            aria-label="Close modal"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <div className="ui-mdc-booking-view-content" style={{ padding: "20px", overflowY: "auto", flex: 1 }}>
            {error && (
              <div className="ui-mdc-booking-error" style={{ marginBottom: "20px" }}>
                <strong>Error:</strong> {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  borderRadius: "4px",
                  marginBottom: "20px",
                }}
              >
                Review submitted successfully! Thank you for your feedback.
              </div>
            )}

            {/* Customer Information */}
            <div className="ui-mdc-booking-view-section" style={{ marginBottom: "20px" }}>
              <h4 className="ui-mdc-booking-view-section-title">Customer Information</h4>
              <div className="ui-mdc-booking-view-grid">
                <div className="ui-mdc-booking-view-item">
                  <label>
                    Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    required
                    style={{
                      color: "black",
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                <div className="ui-mdc-booking-view-item">
                  <label>
                    Email <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                  
                    type="email"
                    name="customerEmail"
                    value={formData.customerEmail}
                    onChange={handleInputChange}
                    required
                    style={{
                      color: "black",
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                <div className="ui-mdc-booking-view-item">
                  <label>
                    Phone <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="tel"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleInputChange}
                    required
                    style={{
                      color: "black",
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Rating */}
            <div className="ui-mdc-booking-view-section" style={{ marginBottom: "20px" }}>
              <h4 className="ui-mdc-booking-view-section-title">
                Rating <span style={{ color: "red" }}>*</span>
              </h4>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleStarClick(star)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                    }}
                    aria-label={`Rate ${star} star${star !== 1 ? "s" : ""}`}
                  >
                    <FiStar
                      size={32}
                      fill={star <= formData.rating ? "#FFD700" : "none"}
                      stroke={star <= formData.rating ? "#FFD700" : "#ccc"}
                      style={{
                        transition: "all 0.2s",
                      }}
                    />
                  </button>
                ))}
                {formData.rating > 0 && (
                  <span style={{ marginLeft: "8px", color: "#666" }}>
                    {formData.rating} / 5
                  </span>
                )}
              </div>
            </div>

            {/* Review Message */}
            <div className="ui-mdc-booking-view-section" style={{ marginBottom: "20px" }}>
              <h4 className="ui-mdc-booking-view-section-title">
                Review Message <span style={{ color: "red" }}>*</span>
              </h4>
              <textarea
                name="reviewMessage"
                value={formData.reviewMessage}
                onChange={handleInputChange}
                required
                rows={4}
                placeholder="Share your experience..."
                style={{
                  color: "black",
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            {/* Booking Reference */}
            <div className="ui-mdc-booking-view-section" style={{ marginBottom: "20px" }}>
              <h4 className="ui-mdc-booking-view-section-title">Booking Reference</h4>
              <input
                type="text"
                name="bookingReference"
                value={formData.bookingReference}
                onChange={handleInputChange}
                readOnly
                style={{
                  color: "black",
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "#f5f5f5",
                }}
              />
            </div>

            {/* Customer Photo – POST /api/images/upload (title, category: "review", image) → url used as review customerPhoto */}
            <div className="ui-mdc-booking-view-section" style={{ marginBottom: "20px" }}>
              <h4 className="ui-mdc-booking-view-section-title">
                Customer Photo <span style={{ color: "red" }}>*</span>
              </h4>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                disabled={isUploadingPhoto}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              />
              {isUploadingPhoto && (
                <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>Uploading photo...</p>
              )}
              {customerPhotoUrl && !isUploadingPhoto && (
                <p style={{ fontSize: "12px", color: "green", marginTop: "8px" }}>Photo uploaded ({customerPhotoUrl})</p>
              )}
              {photoPreview && (
                <div style={{ marginTop: "12px" }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      color: "black",
                      maxWidth: "200px",
                      maxHeight: "200px",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="ui-mdc-confirm-modal-actions" style={{ flexShrink: 0 }}>
            <button
              type="button"
              onClick={onClose}
              className="ui-mdc-confirm-button ui-mdc-confirm-button--secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ui-mdc-confirm-button"
              style={{ backgroundColor: "#c4915f", color: "white" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;

